<template lang="html">
    <div
        class="search-wrapper is-flex"
    >
        <!-- geolocator button -->
        <div
            v-if="locateButton && hasNavigation"
            class="control locate-button"
        >
            <b-tooltip
                v-bind="geolocateTooltipOptions"
            >
                <button
                    class="get-location button is-info"
                    @click="geolocate"
                >
                    <b-icon
                        icon="crosshairs"
                    />
                </button>
            </b-tooltip>
        </div>

        <b-field
            class="search-bar"
            :class="{ 'map-overlay': mapOverlay }"
        >
            <!-- "search type" dropdown -->
            <b-select
                v-if="searchTypes.length > 1"
                v-model="searchType"
                placeholder="Search type"
                @input="searchTerm = ''"
            >
                <option
                    v-for="({ label }, i) in searchTypes"
                    :key="label"
                    :value="i"
                    v-text="label"
                />
            </b-select>

            <!-- search input -->
            <b-autocomplete
                v-model="searchTerm"
                :data="searchData"
                :placeholder="placeholder"
                :icon="icon"
                clear-on-select
                expanded
                :loading="loadingResults"
                @typing="getAsyncData"
                @select="selectResult"
                @input="(value) => $emit('input', value)"
            >
                <template slot-scope="props">
                    <div class="search-result is-flex">
                        <div class="result-title is-flex">
                            <b-icon
                                type="is-primary"
                                size="is-small"
                                pack="mdi"
                                :icon="searchResults.length ? 'map-marker' : 'emoticon-sad-outline'"
                            />
                            <span>{{ props.option.title }}</span>
                        </div>
                        <small
                            class="has-text-grey"
                            style="float: right;"
                        >
                            {{ props.option.subtitle }}
                        </small>
                    </div>
                </template>
            </b-autocomplete>
        </b-field>
    </div>
</template>

<script>
import debounce from 'lodash.debounce';

export default {
    props: {
        // adds the map-overlay class
        mapOverlay: {
            type: Boolean,
            default: true,
        },

        // displays the geolocator button
        locateButton: {
            type: Boolean,
            default: true,
        },

        // search type options for the dropdown menu
        // should be an array of objects with at least a "label"
        searchTypes: {
            type: Array,
            default: () => [
                {
                    label: 'Places',
                    placeholder: 'Search for a location',
                    icon: 'magnify',
                },
                {
                    label: 'Lat Long',
                    placeholder: '51.06881 -114.116207',
                    icon: 'map-marker',
                },
            ],
        },

        // results to display under the input box
        // should be an array of objects with at least a "title" and "subtitle"
        searchResults: {
            type: Array,
            default: () => [],
        },

        // displays a loading indicator if true
        loadingResults: {
            type: Boolean,
            default: false,
        },

        // options for configuring geolocate tooltip
        geolocateTooltipOptions: {
            type: Object,
            required: false,
            default: () => ({
                label: '',
            }),
        },
    },

    data: () => ({
        hasNavigation: ('geolocation' in navigator),
        searchTerm: '',
        // selected search type (index)
        searchType: 0,
    }),

    computed: {
        // selected search type (object)
        searchTypeObj() {
            return this.searchTypes[this.searchType];
        },

        // return custom placeholder, if any, or default "Search..."
        placeholder() {
            if (this.searchTypeObj.placeholder) {
                return this.searchTypeObj.placeholder;
            }

            return 'Search...';
        },

        // return custom icon, if any, or default "magnify"
        icon() {
            if (this.searchTypeObj.icon) {
                return this.searchTypeObj.icon;
            }

            return 'magnify';
        },

        // search data that gets sent to the autocomplete to fill in the dropdown options
        searchData() {
            if (this.loadingResults) {
                return [];
            }

            if (this.searchResults.length === 0) {
                return [{
                    title: 'No search results',
                    defaultText: true,
                }];
            }

            return this.searchResults;
        },
    },

    methods: {
        /**
         * emit "select" event for picking a search result
         * @param  {Object} result search result object
         */
        selectResult(result) {
            if (!result.defaultText) {
                this.$emit('select', result, this.searchTypeObj);
            }
        },

        /**
         * attempt browser geolocation and emit result
         */
        geolocate() {
            if (this.hasNavigation) {
                navigator.geolocation.getCurrentPosition(
                    // success
                    (position) => {
                        this.$emit('geolocation', [position.coords.longitude, position.coords.latitude]);
                        this.$ga.event('Map', 'geolocation-button-pressed', `lat: ${position.coords.latitude}, long: ${position.coords.longitude}`);
                    },
                    // error
                    (error) => {
                        this.$emit('geolocation-error', error);
                    },
                );
            }
        },

        /**
         * emit search query on input (debounced)
         * @param  {String} searchTerm search query term
         */
        getAsyncData: debounce(async function fetchResults(searchTerm) {
            if (searchTerm.length) {
                this.$emit('typing', searchTerm, this.searchTypeObj);
            }
        }, 200),
    },
};
</script>

<style lang="scss">
    .search-wrapper {
        // input group (type select + search input)
        .search-bar {
            flex: 1 1;
            margin-bottom: 2px;

            &.map-overlay {
                height: 2em;
                z-index: 2;
            }
        }

        // geolocation button
        .locate-button {
            margin-right: 0.5em;
        }

        // results list dropdown
        .dropdown-menu {
            min-width: 20vw;
            width: 100%;

            a.dropdown-item {
                padding: 0.4em 1em;
            }
        }

        // individual search result template
        .search-result {
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;

            .result-title {
                align-items: center;

                .icon {
                    margin-right: 0.25em;
                    margin-bottom: -0.175em;
                }
            }
        }
    }
</style>
